// © Microsoft Corporation. All rights reserved.

import Foundation, { FoundationProps } from "@microsoft/fast-components-foundation-react";
import { PerfTracker, getPageTimings, incrementCounter, logger, markTtf, markTtvr } from "@msnews/diagnostics";

import { ExperienceMilestoneEventTypes } from "@msnews/milestone-data";
import { MnsCreatorDashboardAttributes } from "./MnsCreatorDashboard.attributes";
import { appEnvironment } from "@msnews/core";
import { canUseDOM } from "@msnews/isomorphic-util";

declare global {
    interface Window {
        /**
         * Boolean flag to indicate if render cache hydration is happening
         */
        needsHydrate?: boolean;
    }
}

/** Type definition for the onExperienceMilestoneTimingUpdate callback */
export type ExperienceMilestoneTimingUpdate = (milestoneType: ExperienceMilestoneEventTypes,
    startTime?: number,
    endTime?: number) => void;

const pageTimings = getPageTimings();

/**
 * Internal interface used for detecting render counter related static properties
 * in Experience components. We use this to cast the object's constructor to see
 * if the custom render counter prefix is available to use to emit custom page timings.
 * Note that this interface is not meant to be explicitly extended, since we're trying to
 * to leverage static properties from the Experience component classes and interfaces are
 * not supported on static properties. This purely serves as a type definition so we can
 * access the static `renderCounterPrefix` property without typescript complaining about it.
 */
interface ExperienceStaticConstructor {
    /**
     * Optional custom render count marker prefix to use to log an additional count metric
     * in addition to the default render counters. Useful when you want to count a group of
     * similar experiences under a custom render counter group. Currently used by content
     * experiences to log the total number of content card renders before TTVR. This value
     * is used as an prefix value to form the final marker name. For example, a value of
     * "XYZ" would end up logging "XYZExpRenderCount" in addition to the default "ExpRenderCount"
     */
    renderCounterPrefix: string;
}

/**
 * Common props for all experiences
 */
export interface CommonExperienceProps {
    /** The experience name. */
    experienceName?: string;

    /** Custom Field per experience when sending beacon to Cosmos. */
    customField?: string;

    /**
     * Handler function for Experience Milestone timing updates
     */
    onExperienceMilestoneTimingUpdate?: ExperienceMilestoneTimingUpdate
}

/**
 * TODO: This class may need to be sampled collecting and sending Perf timings if the traffic goes beyond a certain reach.
 * Assessment will be performed later after analyzing the data.
 */
export class BaseExperience<H, U, S> extends Foundation<H & CommonExperienceProps, U, S> {
    /** Perf marker for tracking Visually Reading timing */
    protected visuallyReadyTracker: PerfTracker = new PerfTracker();

    /** Perf marker for tracking Initialization timing */
    protected initializedTracker: PerfTracker = new PerfTracker();

    /** Perf marker for tracking DomComplete timing */
    protected domCompleteTracker: PerfTracker = new PerfTracker();

    /**
     * The constructor.
     * @param props - The props
     * @param context - The context
     */
    constructor(props: H & U & CommonExperienceProps & FoundationProps, context?: any) {
        super(props);

        this.startInitializeMilestone();
        this.startVisuallyReadyMilestone();
        this.startDomCompleteMilestone();

        // This tracks the # of experiences that got instantiated on the page before TTVR marker happens.
        this.incrementExpCount("");
    }

    /**
     * Default react hook.
     */
    public componentDidMount(): void {
        // End the domComplete milestone
        this.endDomCompleteMilestone();
    }

    /**
     * Default react hook.
     *
     * note: don't delete it, as it has different arity than the original react hook, and some components call it
     * with arity=0
     *
     * @param prevProps - previous component props
     * @param prevState - previous component state
     * @param snapshot - "snapshot" returned by getSnapshotBeforeUpdate() in React life cycle
     */
    public componentDidUpdate(
        prevProps: Readonly<H & U & FoundationProps> = undefined,
        prevState: Readonly<S> = undefined,
        snapshot: any = undefined
    ): void {

        // End the domComplete milestone
        this.endDomCompleteMilestone();
    }

    /**
     * Default render function
     *
     * note: don't delete it, even if empty, as a bunch of code calls super.render()
     */
    public render(): any {
        logger.log(`Rendering ${(this.props as CommonExperienceProps).experienceName}. needsHydrate: ${canUseDOM() && window.needsHydrate}`);

        // End the initialized milestone, and start the domComplete milestone
        this.endInitializeMilestone();
        this.startDomCompleteMilestone();

        // This tracks the # of times all experiences on the page render before TTVR marker happens.
        this.incrementExpCount("Render");
    }

    /**
     * Marks the Time To Visually Ready and optionally mark perf ttvr for telemetry.
     * @param shouldMarkTelemetryTtvr - Flag to opt out from from telemetry ttvr marker
     * @param aliasName - Optional alias for TTVR marker name
     */
    public markTimeToVisuallyReady(shouldMarkTelemetryTtvr: boolean, aliasName?: string): void {
        if (shouldMarkTelemetryTtvr) {
            var t = window.performance.now();
            markTtvr(this.props.experienceName, false, t);
            if (aliasName) {
                markTtvr(aliasName, false, t);
            }
        }

        this.endVisuallyReadyMilestone();
    }

    /**
     * Mark perf ttf for telemetry.
     * @param aliasName - Optional alias for TTF marker name
     */
    public markTimeToFunctional(aliasName?: string): void {
        var t = window.performance.now();
        markTtf(this.props.experienceName, false, t);
        if (aliasName) {
            markTtf(aliasName, false, t);
        }
    }

    /**
     * Generate attributes for the mnsDashboard
     */
    protected mnsDashAttributes(): MnsCreatorDashboardAttributes {
        if (appEnvironment.ShowMnsCreatorDashboard) {
            const msnCreatorDashAttributes = {
                "data-config-href": this.props["data-config-href"],
                "data-experience-type": this.props["data-experience-type"]
            };
            return msnCreatorDashAttributes;
        } else {
            return null;
        }
    }

    /** Start the initialized perf timer, and mark the Initialized milestone start time */
    protected startInitializeMilestone(): void {
        this.initializedTracker.setStart();
        this.updateStartTiming(this.initializedTracker, ExperienceMilestoneEventTypes.initialized);
    }

    /** End the initialized perf timer, and mark the Initialized milestone end time */
    protected endInitializeMilestone(): void {
        this.updateEndTiming(this.initializedTracker, ExperienceMilestoneEventTypes.initialized);
    }

    /** Start the Visually Reader perf timer, and mark the VisuallyReady milestone start time */
    protected startVisuallyReadyMilestone(): void {
        this.updateStartTiming(this.visuallyReadyTracker, ExperienceMilestoneEventTypes.visuallyReady);
    }

    /** End the Visually Reader perf timer, and mark the VisuallyReady milestone end time */
    protected endVisuallyReadyMilestone(): void {
        this.updateEndTiming(this.visuallyReadyTracker, ExperienceMilestoneEventTypes.visuallyReady);
    }

    /** Start the DomComplete perf timer, and mark the DomComplete milestone start time */
    protected startDomCompleteMilestone(): void {
        this.updateStartTiming(this.domCompleteTracker, ExperienceMilestoneEventTypes.domComplete);
    }

    /** End the DomComplete perf timer, and mark the DomComplete milestone end time */
    protected endDomCompleteMilestone(): void {
        this.updateEndTiming(this.domCompleteTracker, ExperienceMilestoneEventTypes.domComplete);
    }

    /**
     * Helper to update the start timing for a given tracker & event type
     * @param tracker - the {PerfTracker} to update the start time
     * @param eventType - the milestone event type
     */
    private updateStartTiming(tracker: PerfTracker, eventType: ExperienceMilestoneEventTypes): void {

        tracker.setStart();

        const { onExperienceMilestoneTimingUpdate } = this.props;
        if (onExperienceMilestoneTimingUpdate) {
            onExperienceMilestoneTimingUpdate(eventType, tracker.getStart());
        }
    }

    /**
     * Helper to update end timing for a given tracker & event type
     * @param tracker - the {PerfTracker} to update the start time
     * @param eventType - the milestone event type
     */
    private updateEndTiming(tracker: PerfTracker, eventType: ExperienceMilestoneEventTypes): void {

        if (tracker.getEnd() !== -1) {
            return;
        }

        tracker.setEnd();

        const { onExperienceMilestoneTimingUpdate } = this.props;
        if (onExperienceMilestoneTimingUpdate) {
            onExperienceMilestoneTimingUpdate(eventType, undefined, tracker.getEnd());
        }
    }

    /**
     * Helper to increment render counter statistics on the _pageTimings object for a given marker name
     * Note that if the `renderCounterPrefix` static property is present on the Experience's component class,
     * this method will update the render counters for that custom counter name as well.
     * @param markerPrefix - the prefix of the marker to increment.
     */
    protected incrementExpCount(markerPrefix: string): void {

        // prevent counting in SSR and post-TTVR scenarios
        if (!pageTimings || pageTimings["TTVR"]) {
            return;
        }

        incrementCounter(`Exp.${markerPrefix}Count`);

        const renderCounterPrefix = (this.constructor as unknown as ExperienceStaticConstructor).renderCounterPrefix;
        if (renderCounterPrefix) {
            incrementCounter(`Exp.${renderCounterPrefix}${markerPrefix}Count`);
        }
    }
}